/**
 * Microphone Permission Dialog
 * Shows instructions when microphone access is denied
 * Note: This is ONLY shown when permission is explicitly denied
 * For 'prompt' state, browser handles it natively
 */

import React from 'react';
import { createPortal } from 'react-dom';

interface MicrophonePermissionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onPermissionGranted?: () => void; // Not used, kept for compatibility
}

export const MicrophonePermissionDialog: React.FC<MicrophonePermissionDialogProps> = ({
  isOpen,
  onClose,
}) => {
  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
      style={{ zIndex: 20000 }}
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="flex flex-col gap-4 p-6 rounded-lg border border-[var(--color-accent)] bg-[var(--color-secondary)] text-[var(--color-text)] shadow-xl max-w-md w-full mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 mt-1">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold mb-1">Microphone Access Blocked</h3>
            <p className="text-sm opacity-80">
              Voice mode requires microphone access. Please enable it in your browser settings.
            </p>
          </div>
        </div>

        {/* Status message */}
        <div className="p-3 rounded text-sm bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200">
          Microphone access is currently blocked. Follow the steps below to enable it.
        </div>

        {/* Instructions */}
        <div className="text-sm space-y-2">
          <p className="font-medium">To enable microphone access:</p>
          <ol className="list-decimal list-inside space-y-1 text-sm opacity-80 ml-2">
            <li>Click the 🔒 lock icon (or ⓘ info icon) in your browser's address bar</li>
            <li>Find "Microphone" in the permissions list</li>
            <li>Change the setting to "Allow"</li>
            <li>Refresh this page</li>
            <li>Try voice mode again</li>
          </ol>
        </div>

        {/* Action button */}
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            className="px-4 py-2 text-sm rounded bg-[var(--color-accent)] text-white hover:opacity-80"
            onClick={onClose}
          >
            Got It
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default MicrophonePermissionDialog;
